import React, { useEffect, useState, useCallback } from "react";
import { useParams, Link, useNavigate, useSearchParams } from "react-router-dom";
import { DatasetService } from "../../../services/DatasetService";
import { usePermissions } from "../../../PermissionsProvider";
import moment from "moment";
import { FinetuneDataset } from "../../../model_gen/finetuning";
import { ChatCompletionRequest } from "../../../model_gen/chat";
import RequestViewModal from "../../Request/RequestViewModal";
import { ReactComponent as AiStars } from "../../../assets/svg/ai-creation.svg";
import DatasetModification from "./DatasetModification";
import { MultiLayerSemanticChart } from "../../shared/MultiLayerSemanticChart";
import AIReview from "./AIReview";
import ReviewsTable from "../../shared/ReviewsTable";
import AugmentationsTable from "../../shared/AugmentationsTable";
import VersionsTable from "../../shared/VersionsTable";
import DatasetRequestsTable from "./DatasetRequestsTable";
import { Button } from "../../ui/button";
import { Card, CardContent } from "../../ui/card";
import OverflowTabs, { TabItem } from "../../ui/overflow-tabs";
import { 
  EditIcon,
  WrenchIcon,
  FileTextIcon,
  CalendarIcon,
  AppWindowIcon,
  FileSearchIcon,
  FlaskConicalIcon,
  GitBranchIcon,
  Loader2Icon
} from "lucide-react";

interface AIReviewPayload {
  correction_type: string;
  choices?: string[];
}

interface TabDefinition {
  key: string;
  label: string;
  icon: React.ReactNode;
  content: React.ReactNode;
}

const DatasetDetail: React.FC = () => {
  const { id } = useParams();
  const { apiKey } = usePermissions();
  const [dataset, setDataset] = useState<FinetuneDataset | null>(null);
  const [requests, setRequests] = useState<ChatCompletionRequest[]>([]);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [isRequestsLoading, setIsRequestsLoading] = useState<boolean>(false);
  const [currentPage, setCurrentPage] = useState<number>(1);
  const [requestPageSize, setRequestPageSize] = useState<number>(10);
  const [requestsByPage, setRequestsByPage] = useState<
    Record<number, ChatCompletionRequest[]>
  >({});
  const [totalRequests, setTotalRequests] = useState<number>(0);
  const [isModificationModalVisible, setIsModificationModalVisible] =
    useState<boolean>(false);
  const [isModalVisible, setIsModalVisible] = useState(false);
  const [selectedRequest, setSelectedRequest] =
    useState<ChatCompletionRequest | null>(null);
  const [activeTab, setActiveTab] = useState<string>("overview");
  const [isAiReviewModalVisible, setIsAiReviewModalVisible] = useState(false);
  const [isAiReviewLoading, setIsAiReviewLoading] = useState(false);
  const [reviews, setReviews] = useState<any[]>([]);
  const [isReviewsLoading, setIsReviewsLoading] = useState<boolean>(false);
  const [reviewsCurrentPage, setReviewsCurrentPage] = useState<number>(1);
  const [reviewsPageSize, setReviewsPageSize] = useState<number>(10);
  const [totalReviews, setTotalReviews] = useState<number>(0);
  const [reviewsByPage, setReviewsByPage] = useState<Record<number, any[]>>({});
  const [augmentations, setAugmentations] = useState<any[]>([]);
  const [isAugmentationsLoading, setIsAugmentationsLoading] = useState<boolean>(false);
  const [augmentationsCurrentPage, setAugmentationsCurrentPage] = useState<number>(1);
  const [augmentationsPageSize, setAugmentationsPageSize] = useState<number>(10);
  const [totalAugmentations, setTotalAugmentations] = useState<number>(0);
  const [reviewProgress, setReviewProgress] = useState<Record<string, any>>({});
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [versions, setVersions] = useState<any[]>([]);
  const [versionsLoading, setVersionsLoading] = useState<boolean>(false);

  const handleViewRequest = (request: ChatCompletionRequest) => {
    setSelectedRequest(request);
    setIsModalVisible(true);
  };

  const handleCloseModal = () => {
    setIsModalVisible(false);
    setSelectedRequest(null);
  };

  // Fetch dataset details
  useEffect(() => {
    if (id) {
      setIsLoading(true);
      DatasetService.getDataset(Number(id), setIsLoading, apiKey).subscribe(
        (data: FinetuneDataset) => {
          if (data) {
            setDataset(data);
          } else {
            console.error("Received empty dataset data");
          }
        },
        (error: any) => {
          console.error("Error fetching dataset:", error);
        }
      );
    }
  }, [id, apiKey]);

  // Fetch dataset requests
  useEffect(() => {
    if (id && activeTab === "requests" && !requestsByPage[currentPage]) {
      fetchRequests(currentPage);
    } else if (activeTab === "requests" && requestsByPage[currentPage]) {
      setRequests(requestsByPage[currentPage]);
    }
  }, [id, currentPage, requestPageSize, requestsByPage, activeTab]);

  const fetchRequests = (page: number) => {
    const offset = (page - 1) * requestPageSize;
    setIsRequestsLoading(true);

    DatasetService.getDatasetRequests(
      Number(id),
      offset,
      requestPageSize,
      setIsRequestsLoading,
      apiKey
    ).subscribe(
      (response: { items: ChatCompletionRequest[]; total: number }) => {
        setRequests(response.items);
        setTotalRequests(response.total);
        setRequestsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
        setIsRequestsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching dataset requests:", error);
        setIsRequestsLoading(false);
      }
    );
  };

  // Fetch dataset reviews
  useEffect(() => {
    if (id && activeTab === "reviews" && !reviewsByPage[reviewsCurrentPage]) {
      fetchReviews(reviewsCurrentPage);
    } else if (activeTab === "reviews" && reviewsByPage[reviewsCurrentPage]) {
      setReviews(reviewsByPage[reviewsCurrentPage]);
    }
  }, [id, apiKey, reviewsCurrentPage, reviewsPageSize, activeTab]);

  const fetchReviews = (page: number) => {
    if (!id) return;

    setIsReviewsLoading(true);
    const offset = (page - 1) * reviewsPageSize;

    DatasetService.getDatasetReviews(
      Number(id),
      offset,
      reviewsPageSize,
      setIsReviewsLoading,
      apiKey
    ).subscribe(
      (response: { items: any[]; total: number }) => {
        setReviews(response.items || []);
        setTotalReviews(response.total);
        setReviewsByPage((prev) => ({
          ...prev,
          [page]: response.items,
        }));
      },
      (error: any) => {
        console.error("Error fetching dataset reviews:", error);
      }
    );
  };

  const handleStartAiReview = (payload: AIReviewPayload) => {
    if (!id) return;

    setIsAiReviewLoading(true);
    DatasetService.startAiReview(
      Number(id),
      setIsAiReviewLoading,
      apiKey,
      payload
    ).subscribe(
      (response: any) => {
        console.log("AI review started successfully", response);
        setIsAiReviewModalVisible(false);

        // Update the dataset locally to show review is in progress
        if (dataset) {
          setDataset({
            ...dataset,
            meta: {
              ...dataset.meta,
              review_status: "CREATED",
            },
          });
        }
      },
      (error: any) => {
        console.error("Error starting AI review:", error);
      }
    );
  };

  const handleRetryReview = (reviewId: number) => {
    if (window.confirm("Are you sure you want to retry this review?")) {
      DatasetService.retryDatasetReview(reviewId, null, apiKey).subscribe(
        () => {
          console.log("Review retry started");
          // Clear the reviews page cache to force a refresh
          setReviewsByPage({});
          // Reset to first page and fetch fresh data
          setReviewsCurrentPage(1);
          fetchReviews(1);
        },
        (error: any) => {
          console.error("Error retrying review:", error);
        }
      );
    }
  };

  useEffect(() => {
    const fetchProgress = () => {
      if (!id) return;

      DatasetService.getDatasetReviewProgressByDataset(
        Number(id),
        null,
        apiKey
      ).subscribe(
        (progressData: Record<string, any>) => {
          setReviewProgress(progressData);
        },
        (error: any) => {
          console.error(error);
        }
      );
    };

    // Always fetch progress once when component mounts
    fetchProgress();

    // Check if any reviews need monitoring
    const hasIncompleteReviews = reviews.some(
      (review) => review.status !== "COMPLETED" && review.status !== "ERROR"
    );

    // Set up interval only if there are incomplete reviews
    let intervalId: NodeJS.Timeout | undefined;
    if (hasIncompleteReviews) {
      intervalId = setInterval(fetchProgress, 5000);
    }

    // Cleanup on unmount
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [id, apiKey, reviews]);

  // Add useEffect for fetching augmentations
  const fetchAugmentations = useCallback((page: number) => {
    if (!id) return;
    const offset = (page - 1) * augmentationsPageSize;
    setIsAugmentationsLoading(true);

    DatasetService.getDatasetAugmentations(
      Number(id),
      offset,
      augmentationsPageSize,
      setIsAugmentationsLoading,
      apiKey
    ).subscribe(
      (response: { items: any[]; total: number }) => {
        setAugmentations(response.items || []);
        setTotalAugmentations(response.total);
        setIsAugmentationsLoading(false);
      },
      (error: any) => {
        console.error("Error fetching augmentations:", error);
        setIsAugmentationsLoading(false);
      }
    );
  }, [id, augmentationsPageSize, apiKey]);

  useEffect(() => {
    if (id && activeTab === "augmentations") {
      fetchAugmentations(augmentationsCurrentPage);
    }
  }, [id, augmentationsCurrentPage, activeTab, fetchAugmentations]);

  // Add useEffect for polling augmentation status
  useEffect(() => {
    // Only poll if we're on the augmentations tab
    if (activeTab !== "augmentations") return;

    // Find any augmentations that are still running
    const runningAugmentations = augmentations.filter(
      aug => aug.status === "RUNNING" || aug.status === "CREATED"
    );

    // If no running augmentations, don't poll
    if (runningAugmentations.length === 0) return;

    const pollInterval = setInterval(() => {
      // For each running augmentation, check its status
      runningAugmentations.forEach(aug => {
        DatasetService.getAugmentationDetails(
          aug.augmentation_id,
          null,
          apiKey
        ).subscribe(
          (details: any) => {
            // If status changed to completed or error, refresh the table
            if (details.status === "COMPLETED" || details.status === "ERROR") {
              setAugmentationsCurrentPage(1);
              fetchAugmentations(1);
            }
          },
          (error: any) => {
            console.error("Error checking augmentation status:", error);
          }
        );
      });
    }, 5000);

    return () => clearInterval(pollInterval);
  }, [activeTab, augmentations, apiKey, fetchAugmentations]);

  // Update useEffect to set initial tab from URL
  useEffect(() => {
    const tabFromUrl = searchParams.get("tab");
    if (tabFromUrl) {
      setActiveTab(tabFromUrl);
    }
  }, [searchParams]);

  // Fetch versions when the versions tab is activated
  useEffect(() => {
    if (id && activeTab === "versions" && versions.length === 0) {
      setVersionsLoading(true);
      DatasetService.getDatasetVersions(Number(id), setVersionsLoading, apiKey).subscribe(
        (data: any[]) => {
          if (data) {
            setVersions(data);
          } else {
            console.error("Received empty versions data");
          }
        },
        (error: any) => {
          console.error("Error fetching versions:", error);
        }
      );
    }
  }, [id, apiKey, activeTab, versions.length]);

  const formatDate = (timestamp: number) => {
    if (!timestamp || timestamp <= 0) return "Never";
    const date = moment(timestamp);
    return date.format("MMM D, YYYY");
  };

  const renderOverviewTab = () => (
    <div className="space-y-8">
      {/* Stats Row */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <FileTextIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">Total Requests</p>
              </div>
              <p className="text-3xl font-gc-demi-bold text-foreground">
                {dataset?.request_count || 0}
              </p>
            </div>
          </CardContent>
        </Card>
        
        <Card className="border-border">
          <CardContent className="p-6">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <CalendarIcon className="w-4 h-4 text-muted-foreground" />
                <p className="text-sm text-muted-foreground font-gc-reg">Date Created</p>
              </div>
              <p className="text-lg font-gc-med text-foreground">
                {dataset?.date_created ? moment(dataset.date_created).format("MMM D, YYYY") : "-"}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Details Section */}
      <Card className="border-border">
        <CardContent className="p-6">
          <h3 className="text-lg font-gc-med text-foreground mb-4">Details</h3>
          <div className="space-y-6">
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">Description</p>
              <p className="text-base font-gc-reg text-foreground whitespace-pre-wrap">
                {dataset?.description || "No description provided"}
              </p>
            </div>
            <div>
              <p className="text-sm text-muted-foreground font-gc-reg mb-1">Source</p>
              <div className="text-base font-gc-reg text-foreground">
                {dataset?.intent_group_name &&
                !dataset?.application_id &&
                !dataset?.intent_id ? (
                  <Link
                    to={`/intent-groups/${dataset?.intent_group_id}`}
                    className="text-maitai-green hover:underline"
                  >
                    {dataset?.intent_group_name}
                  </Link>
                ) : dataset?.application_name &&
                  dataset?.intent_name &&
                  !dataset?.intent_group_id ? (
                  <>
                    <Link
                      to={`/application/${dataset?.application_id}`}
                      className="text-maitai-green hover:underline"
                    >
                      {dataset?.application_name}
                    </Link>
                    {" / "}
                    <Link
                      to={`/application/${dataset?.application_id}/intent/${dataset?.intent_id}`}
                      className="text-maitai-green hover:underline"
                    >
                      {dataset?.intent_name}
                    </Link>
                  </>
                ) : (
                  "Unknown source"
                )}
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Semantic Distribution Chart */}
      {dataset && (
        <MultiLayerSemanticChart
          chartData={[
            {
              entityType: "dataset",
              entityTypeId: id || "",
              name: dataset.name,
            },
          ]}
          apiKey={apiKey}
        />
      )}
    </div>
  );

  const renderRequestsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Requests</h3>
          <p className="text-sm text-muted-foreground">View and manage dataset requests</p>
        </div>
                 <Button
           onClick={() => setIsModificationModalVisible(true)}
           className="gap-2"
         >
           <WrenchIcon className="w-4 h-4" />
           Broad Modification
         </Button>
      </div>

      <DatasetRequestsTable
        requests={requests}
        requestsTotal={totalRequests}
        currentPage={currentPage}
        pageSize={requestPageSize}
        loading={isRequestsLoading}
        onPageChange={setCurrentPage}
        onRowClick={handleViewRequest}
        apiKey={apiKey}
        emptyTitle="No requests found"
        emptyDescription="Add requests to this dataset to get started."
      />
    </div>
  );

  const renderReviewsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Reviews</h3>
          <p className="text-sm text-muted-foreground">Manage AI reviews and corrections</p>
        </div>
        <Button
          onClick={() => {
            const reviewStatus = dataset?.meta?.review_status as string | undefined;
            const hasProcessingReview = reviews.some(
              (review) => review.status === "PROCESSING"
            );
            if (
              ["CREATED", "SCHEDULED", "PROCESSING", "PENDING"].includes(
                reviewStatus || ""
              ) ||
              hasProcessingReview
            ) {
              console.log("Review already in progress");
            } else {
              setIsAiReviewModalVisible(true);
            }
          }}
          disabled={
            ["CREATED", "SCHEDULED", "PROCESSING", "PENDING"].includes(
              (dataset?.meta?.review_status as string) || ""
            ) || reviews.some((review) => review.status === "PROCESSING")
          }
          className="gap-2"
        >
          {isAiReviewLoading && <Loader2Icon className="w-4 h-4 animate-spin" />}
          <AiStars className="w-4 h-4" />
          AI Review
        </Button>
      </div>

      <ReviewsTable
        reviews={reviews}
        loading={isReviewsLoading}
        currentPage={reviewsCurrentPage}
        totalPages={Math.ceil(totalReviews / reviewsPageSize)}
        totalItems={totalReviews}
        itemsPerPage={reviewsPageSize}
        onPageChange={setReviewsCurrentPage}
        onRetryReview={handleRetryReview}
        reviewProgress={reviewProgress}
      />
    </div>
  );

  const renderAugmentationsTab = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-gc-med text-foreground">Augmentations</h3>
          <p className="text-sm text-muted-foreground">Generate additional training data</p>
        </div>
                 <Button
           onClick={() => navigate(`/forge/datasets/${id}/edit`)}
           className="gap-2"
         >
           <FlaskConicalIcon className="w-4 h-4" />
           New Augmentation
         </Button>
      </div>

      <AugmentationsTable
        augmentations={augmentations}
        loading={isAugmentationsLoading}
        currentPage={augmentationsCurrentPage}
        totalPages={Math.ceil(totalAugmentations / augmentationsPageSize)}
        totalItems={totalAugmentations}
        itemsPerPage={augmentationsPageSize}
        onPageChange={setAugmentationsCurrentPage}
        datasetId={id || ""}
      />
    </div>
  );

  const renderVersionsTab = () => (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-gc-med text-foreground">Version History</h3>
        <p className="text-sm text-muted-foreground">All versions of this dataset, ordered by creation date</p>
      </div>

      <VersionsTable
        versions={versions}
        loading={versionsLoading}
        currentVersionId={id || ""}
      />
    </div>
  );

     // Tab configuration
   const tabDefinitions: TabDefinition[] = [
     {
       key: "overview",
       label: "Overview",
       icon: <AppWindowIcon className="w-4 h-4" />,
       content: renderOverviewTab()
     },
     {
       key: "requests",
       label: "Requests",
       icon: <FileSearchIcon className="w-4 h-4" />,
       content: renderRequestsTab()
     },
     {
       key: "reviews",
       label: "Reviews",
       icon: <FileSearchIcon className="w-4 h-4" />,
       content: renderReviewsTab()
     },
     {
       key: "augmentations",
       label: "Augmentations",
       icon: <FlaskConicalIcon className="w-4 h-4" />,
       content: renderAugmentationsTab()
     },
     {
       key: "versions",
       label: "Versions",
       icon: <GitBranchIcon className="w-4 h-4" />,
       content: renderVersionsTab()
     }
   ];

  // Create tabs array for OverflowTabs component
  const tabs: TabItem[] = tabDefinitions.map(tab => ({
    key: tab.key,
    label: tab.label,
    icon: tab.icon
  }));

  if (isLoading) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
          {/* Header Card Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="h-4 bg-muted rounded w-32 animate-pulse mb-6" />
              <div className="space-y-2">
                <div className="h-10 bg-muted rounded w-64 animate-pulse" />
                <div className="h-5 bg-muted rounded w-96 animate-pulse" />
              </div>
            </CardContent>
          </Card>

          {/* Content Loading */}
          <Card className="border-border">
            <CardContent className="p-8">
              <div className="space-y-4">
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="flex items-center gap-4">
                    <div className="h-4 bg-muted rounded flex-1 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-24 animate-pulse" />
                    <div className="h-4 bg-muted rounded w-8 animate-pulse" />
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-6xl mx-auto px-8 py-8 space-y-8">
        
        {/* Header Card */}
        <Card className="border-border">
          <CardContent className="p-8">
            {/* Breadcrumb Navigation */}
            <nav className="mb-6">
              <ol className="flex items-center gap-2 text-sm">
                <li>
                  <button 
                    onClick={() => navigate("/forge/compositions?tab=datasets")}
                    className="text-muted-foreground hover:text-maitai-green transition-colors font-gc-reg"
                  >
                    Datasets
                  </button>
                </li>
                <li className="text-muted-foreground/40">
                  <span>/</span>
                </li>
                <li>
                  <span className="text-maitai-green font-gc-med">{dataset?.name}</span>
                </li>
              </ol>
            </nav>

            {/* Page Header */}
            <div className="flex items-center justify-between">
              <div className="space-y-1">
                <div className="flex items-center gap-3">
                  <h1 className="text-4xl font-gc-demi-bold text-foreground tracking-tight">
                    {dataset?.name}
                  </h1>
                  <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-blue-100 text-blue-800 dark:bg-blue-900/20 dark:text-blue-400">
                    v{dataset?.major_version || 1}.{dataset?.minor_version || 0}
                  </span>
                </div>
                <p className="text-lg text-muted-foreground font-gc-reg">
                  Manage dataset requests, run fine-tuning, and review model improvements
                </p>
              </div>
              <Button
                onClick={() => navigate(`/forge/datasets/${id}/edit`)}
                className="gap-2"
              >
                <EditIcon className="w-4 h-4" />
                Edit Dataset
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Card className="border-border">
          <CardContent className="p-0">
            {/* Tab Navigation */}
            <OverflowTabs 
              tabs={tabs}
              activeTab={activeTab}
              onTabChange={setActiveTab}
              className="border-b border-border px-8 py-4"
            />

            {/* Tab Content */}
            <div className="p-8">
              {tabDefinitions.find(tab => tab.key === activeTab)?.content}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Dataset Modification Modal */}
      {dataset && (
        <DatasetModification
          datasetId={Number(id)}
          isVisible={isModificationModalVisible}
          onClose={() => setIsModificationModalVisible(false)}
        />
      )}

      {/* Request View Modal */}
      {selectedRequest && (
        <RequestViewModal
          request={selectedRequest}
          isVisible={isModalVisible}
          onClose={handleCloseModal}
          apiKey={apiKey}
        />
      )}

      {/* AI Review Confirmation Modal */}
      <AIReview
        isVisible={isAiReviewModalVisible}
        onClose={() => setIsAiReviewModalVisible(false)}
        onStartReview={handleStartAiReview}
        loading={isAiReviewLoading}
      />
    </div>
  );
};

export default DatasetDetail;
